import {
  Box,
  Grid,
  Typography,
  Card,
  CardContent,
  Container,
  Fade,
  Snackbar,
  Alert,
  styled,
  Chip,
  Divider,
  CircularProgress,
  InputAdornment,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Pagination,
  Stack,
  Button,
  Switch,
  FormControlLabel,
} from "@mui/material";
import React, { useEffect, useState, useMemo, useCallback } from "react";

import { useDispatch, useSelector } from "react-redux";
import {
  Receipt,
  CreditCard,
  Money,
  CalendarToday,
  Search,
  FilterList,
  AccountBalance,
  Payment,
  DateRange,
  Refresh,
  AssignmentInd,
  Download,
} from "@mui/icons-material";
import axios from "axios";
import { apiUrl } from "../../constants";
import { IdCard } from "lucide-react";
const BILLS_PER_PAGE = 30;

const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: "100vh",
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  paddingTop: "40px",
  paddingBottom: "2rem",
  width: "100%",
}));

const BillCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.2)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  transition: "all 0.3s ease-in-out",
  cursor: "pointer",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.15)",
    borderColor: "rgba(103, 126, 234, 0.4)",
  },
}));

const FilterCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  marginBottom: "2rem",
  padding: "1rem",
}));

const PaginationCard = styled(Card)(({ theme }) => ({
  background: "rgba(255, 255, 255, 0.95)",
  backdropFilter: "blur(10px)",
  borderRadius: "12px",
  border: "1px solid rgba(255, 255, 255, 0.3)",
  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
  marginTop: "2rem",
  padding: "1rem",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    background: "rgba(255, 255, 255, 0.8)",
    "& fieldset": {
      borderColor: "rgba(103, 126, 234, 0.3)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(103, 126, 234, 0.5)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#667eea",
    },
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: "8px",
  background: "rgba(255, 255, 255, 0.8)",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(103, 126, 234, 0.3)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(103, 126, 234, 0.5)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#667eea",
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: "white",
  fontWeight: "700",
  marginBottom: "2rem",
  textAlign: "center",
  textShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
}));

const AmountChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  fontWeight: "600",
  fontSize: "0.9rem",
  padding: "4px 8px",
  "& .MuiChip-label": {
    paddingLeft: "12px",
    paddingRight: "12px",
  },
}));

const PaymentTypeChip = styled(Chip)(({ paymentType, theme }) => ({
  background:
    paymentType === "Online"
      ? "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)"
      : paymentType === "Cash"
        ? "linear-gradient(135deg, #059669 0%, #0d9488 100%)"
        : paymentType === "RAZORPAY"
          ? "linear-gradient(135deg, #1e40af 0%, #1d4ed8 100%)"
          : paymentType === "Cash Online"
            ? "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)"
            : "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
  color: "white",
  fontWeight: "500",
  fontSize: "0.8rem",
}));

const GstChip = styled(Chip)(({ theme }) => ({
  background: "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
  color: "white",
  fontWeight: "500",
  fontSize: "0.75rem",
}));

const StyledPagination = styled(Pagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    color: "#667eea",
    borderColor: "#667eea",
    "&:hover": {
      backgroundColor: "rgba(103, 126, 234, 0.1)",
    },
    "&.Mui-selected": {
      backgroundColor: "#667eea",
      color: "white",
      "&:hover": {
        backgroundColor: "#5a67d8",
      },
    },
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  "& .MuiFormControlLabel-label": {
    fontWeight: "600",
    color: "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  "& .MuiSwitch-root": {
    "& .MuiSwitch-switchBase": {
      "&.Mui-checked": {
        color: "#059669",
        "& + .MuiSwitch-track": {
          backgroundColor: "#059669",
        },
      },
    },
  },
}));

const BillsManagement = () => {
  const { token } = useSelector((state) => state.userSlice);
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");
  const [gstBillsOnly, setGstBillsOnly] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Debounced search to improve performance
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchBills();
  }, []);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [paymentFilter, dateFilter, gstBillsOnly]);

  const fetchBills = async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${apiUrl}/bill/billwithuser`, {
        headers: { token },
      });
      console.log(`Loaded ${resp.data.data?.length || 0} bills`);
      setBills(resp.data.data || []);
      setLoading(false);
    } catch (error) {
      setToast({
        open: true,
        message: "Failed to fetch bills",
        severity: "error",
      });
      setBills([]);
      setLoading(false);
    }
  };

  // Memoized filtered bills to improve performance
  const filteredBills = useMemo(() => {
    if (!Array.isArray(bills)) {
      return [];
    }

    let filtered = [...bills];

    // GST filter - show only bills with CustGstNumber !== "NA"
    if (gstBillsOnly) {
      filtered = filtered.filter((bill) => {
        const custGstNumber = bill.CustGstNumber || "NA";
        return custGstNumber !== "NA" && custGstNumber.trim() !== "";
      });
    }

    // Search filter
    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter((bill) => {
        const sno = bill.sno?.toString().toLowerCase() || "";
        const id = bill._id?.toLowerCase() || "";
        const date = bill.Date || "";
        const gstNumber = bill.CustGstNumber?.toLowerCase() || "";
        const name = bill.name?.toLowerCase() || "";
        const phoneNo = bill.phone_no?.toString().toLowerCase() || "";

        return (
          sno.includes(searchLower) ||
          id.includes(searchLower) ||
          date.includes(debouncedSearchTerm) ||
          gstNumber.includes(searchLower) ||
          name.includes(searchLower) ||
          phoneNo.includes(searchLower)
        );
      });
    }

    // Payment type filter
    if (paymentFilter !== "all") {
      filtered = filtered.filter((bill) => {
        const paymentType = bill.paymentType?.toLowerCase() || "";
        return paymentType === paymentFilter.toLowerCase();
      });
    }

    // Date filter
    if (dateFilter) {
      filtered = filtered.filter((bill) => {
        return bill.Date === dateFilter;
      });
    }

    return filtered;
  }, [bills, debouncedSearchTerm, paymentFilter, dateFilter, gstBillsOnly]);

  // Memoized paginated bills
  const paginatedBills = useMemo(() => {
    const startIndex = (currentPage - 1) * BILLS_PER_PAGE;
    const endIndex = startIndex + BILLS_PER_PAGE;
    return filteredBills.slice(startIndex, endIndex);
  }, [filteredBills, currentPage]);

  // Calculate total pages
  const totalPages = Math.ceil(filteredBills.length / BILLS_PER_PAGE);

  // Count GST bills
  const gstBillsCount = useMemo(() => {
    return bills.filter((bill) => {
      const custGstNumber = bill.CustGstNumber || "NA";
      return custGstNumber !== "NA" && custGstNumber.trim() !== "";
    }).length;
  }, [bills]);

  const formatCurrency = useCallback((amount) => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;
    return `₹${numAmount.toLocaleString("en-IN")}`;
  }, []);

  const formatDate = useCallback((dateStr) => {
    // Input format is "8/16/2025" (M/D/YYYY)
    const [month, day, year] = dateStr.split("/");
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }, []);

  const getPaymentIcon = useCallback((paymentType) => {
    switch (paymentType?.toLowerCase()) {
      case "online":
        return <CreditCard sx={{ fontSize: "1.2rem" }} />;
      case "cash":
        return <Money sx={{ fontSize: "1.2rem" }} />;
      case "razorpay":
        return <Payment sx={{ fontSize: "1.2rem" }} />;
      case "cash online":
        return <AccountBalance sx={{ fontSize: "1.2rem" }} />;
      default:
        return <Receipt sx={{ fontSize: "1.2rem" }} />;
    }
  }, []);

  const hasGstNumber = useCallback((bill) => {
    const custGstNumber = bill.CustGstNumber || "NA";
    return custGstNumber !== "NA" && custGstNumber.trim() !== "";
  }, []);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
    // Smooth scroll to top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRefresh = () => {
    fetchBills();
  };

  const downloadAllGstBills = async () => {
    try {
      const response = await axios.get(
        `${apiUrl}/bill/adjustedbillcustgstinexcel`,
        {
          responseType: "blob",
          headers: {
            token,
          },
        },
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "FUNINGO_CUSTGST_BILLS.xlsx");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading Excel file:", error);
      alert("Failed to download Excel file. Please try again.");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setPaymentFilter("all");
    setDateFilter("");
    setGstBillsOnly(false);
    setCurrentPage(1);
  };

  return (
    <StyledContainer>
      <Container maxWidth="lg">
        <SectionTitle variant="h4">
          <Receipt sx={{ mr: 1, verticalAlign: "middle" }} />
          Bills Management
        </SectionTitle>

        {/* Filters */}
        <FilterCard>
          {/* GST Toggle Row */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "rgba(5, 150, 105, 0.1)",
              borderRadius: "8px",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <StyledFormControlLabel
                  control={
                    <Switch
                      checked={gstBillsOnly}
                      onChange={(e) => setGstBillsOnly(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <AssignmentInd
                        sx={{ fontSize: "1.2rem", color: "#059669" }}
                      />
                      Show GST Claimed Bills
                    </Box>
                  }
                />
                {gstBillsOnly && (
                  <Button
                    variant="contained"
                    size="small"
                    onClick={downloadAllGstBills}
                    disabled={loading}
                    sx={{
                      background:
                        "linear-gradient(135deg, #059669 0%, #0d9488 100%)",
                      color: "white",
                      "&:hover": {
                        background:
                          "linear-gradient(135deg, #047857 0%, #0f766e 100%)",
                      },
                    }}
                  >
                    Downlod Gst Bill
                  </Button>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                    gap: 3,
                  }}
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#059669", fontWeight: "600" }}
                    >
                      {gstBillsCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      GST Bills
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#6b7280", fontWeight: "600" }}
                    >
                      {bills.length - gstBillsCount}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      Non-GST Bills
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Other Filters */}
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <StyledTextField
                fullWidth
                placeholder="Search by Nmae,Phone,S.No, Bill ID, GST No..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: "#667eea" }} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth>
                <InputLabel>Payment Type</InputLabel>
                <StyledSelect
                  value={paymentFilter}
                  onChange={(e) => setPaymentFilter(e.target.value)}
                  label="Payment Type"
                  startAdornment={
                    <InputAdornment position="start">
                      <FilterList sx={{ color: "#667eea", ml: 1 }} />
                    </InputAdornment>
                  }
                >
                  <MenuItem value="all">All Types</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                  <MenuItem value="razorpay">Razorpay</MenuItem>
                  <MenuItem value="cash online">Cash Online</MenuItem>
                </StyledSelect>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <StyledTextField
                fullWidth
                type="date"
                label="Filter by Date"
                value={
                  dateFilter
                    ? new Date(
                        dateFilter.split("/")[2],
                        dateFilter.split("/")[0] - 1,
                        dateFilter.split("/")[1],
                      )
                        .toISOString()
                        .split("T")[0]
                    : ""
                }
                onChange={(e) => {
                  if (e.target.value) {
                    const date = new Date(e.target.value);
                    const formattedDate = `${
                      date.getMonth() + 1
                    }/${date.getDate()}/${date.getFullYear()}`;
                    setDateFilter(formattedDate);
                  } else {
                    setDateFilter("");
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DateRange sx={{ color: "#667eea" }} />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={clearFilters}
                  sx={{
                    borderColor: "#667eea",
                    color: "#667eea",
                    "&:hover": {
                      borderColor: "#5a67d8",
                      backgroundColor: "rgba(103, 126, 234, 0.1)",
                    },
                  }}
                >
                  Back
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleRefresh}
                  sx={{
                    borderColor: "#667eea",
                    color: "#667eea",
                    "&:hover": {
                      borderColor: "#5a67d8",
                      backgroundColor: "rgba(103, 126, 234, 0.1)",
                    },
                  }}
                >
                  <Refresh sx={{ fontSize: "1rem" }} />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#667eea", fontWeight: "600" }}
                    >
                      {bills.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      Total Bills
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#059669", fontWeight: "600" }}
                    >
                      {filteredBills.length}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      Filtered
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="h6"
                      sx={{ color: "#f59e0b", fontWeight: "600" }}
                    >
                      {currentPage}/{totalPages || 1}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#6b7280" }}>
                      Page
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </FilterCard>

        {/* Loading State */}
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress sx={{ color: "white" }} size={50} />
          </Box>
        )}

        {/* Bills Grid */}
        {!loading && (
          <Grid container spacing={3}>
            {paginatedBills.map((bill, index) => (
              <Grid item xs={12} sm={6} lg={4} key={bill._id}>
                <Fade in={true} timeout={300 + index * 50}>
                  <BillCard>
                    <CardContent sx={{ p: 3 }}>
                      {/* Header */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Receipt
                            sx={{ color: "#667eea", fontSize: "1.5rem" }}
                          />
                          <Typography
                            variant="h6"
                            sx={{ fontWeight: "600", color: "#1f2937" }}
                          >
                            #{bill.sno}
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {hasGstNumber(bill) && (
                            <GstChip
                              label="GST"
                              icon={
                                <AssignmentInd sx={{ fontSize: "0.8rem" }} />
                              }
                              size="small"
                            />
                          )}
                          <PaymentTypeChip
                            paymentType={bill.paymentType}
                            label={bill.paymentType}
                            icon={getPaymentIcon(bill.paymentType)}
                            size="small"
                          />
                        </Box>
                      </Box>

                      {/* Date */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <CalendarToday
                          sx={{ color: "#6b7280", fontSize: "1rem" }}
                        />
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {formatDate(bill.Date)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <IdCard sx={{ color: "#6b7280", fontSize: "1rem" }} />
                        <Typography variant="body2" sx={{ color: "#6b7280" }}>
                          {bill.name}
                        </Typography>
                      </Box>

                      {/* GST Number if exists */}
                      {hasGstNumber(bill) && (
                        <Box
                          sx={{
                            mb: 2,
                            p: 1,
                            background: "rgba(5, 150, 105, 0.1)",
                            borderRadius: "6px",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#059669",
                              fontSize: "0.75rem",
                              fontWeight: "500",
                            }}
                          >
                            GST: {bill.CustGstNumber}
                          </Typography>
                        </Box>
                      )}

                      <Divider sx={{ my: 2, opacity: 0.3 }} />

                      {/* Amount Details */}
                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        {/* Show cash and online amounts separately if both exist */}
                        {bill.CashAmount > 0 && bill.OnlineAmount > 0 ? (
                          <>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                Cash Amount
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "600", color: "#059669" }}
                              >
                                {formatCurrency(bill.CashAmount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                Online Amount
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "600", color: "#4f46e5" }}
                              >
                                {formatCurrency(bill.OnlineAmount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                CGST
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "500", color: "#f59e0b" }}
                              >
                                {formatCurrency(bill.cgst)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                SGST
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "500", color: "#f59e0b" }}
                              >
                                {formatCurrency(bill.sgst)}
                              </Typography>
                            </Grid>
                          </>
                        ) : (
                          <>
                            <Grid item xs={6}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                Base Amount
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "600", color: "#374151" }}
                              >
                                {formatCurrency(bill.amount)}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                CGST
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "500", color: "#f59e0b" }}
                              >
                                {formatCurrency(bill.cgst)}
                              </Typography>
                            </Grid>
                            <Grid item xs={3}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                SGST
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "500", color: "#f59e0b" }}
                              >
                                {formatCurrency(bill.sgst)}
                              </Typography>
                            </Grid>
                          </>
                        )}
                      </Grid>

                      {/* Show discount if exists */}
                      {bill.discount > 0 && (
                        <>
                          <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={8}>
                              <Typography
                                variant="caption"
                                sx={{ color: "#6b7280", fontWeight: "500" }}
                              >
                                Discount ({bill.discountType})
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: "500", color: "#dc2626" }}
                              >
                                -{formatCurrency(bill.discount)}
                              </Typography>
                            </Grid>
                          </Grid>
                        </>
                      )}

                      <Divider sx={{ my: 2, opacity: 0.3 }} />

                      {/* Total */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: "600", color: "#374151" }}
                        >
                          Total Amount
                        </Typography>
                        <AmountChip
                          label={formatCurrency(bill.total)}
                          icon={<Money sx={{ fontSize: "1rem" }} />}
                        />
                      </Box>

                      {/* Biller Phone */}
                      <Box
                        sx={{
                          mt: 2,
                          p: 1,
                          background: "rgba(103, 126, 234, 0.1)",
                          borderRadius: "6px",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ color: "#072092", fontSize: "0.75rem" }}
                        >
                          Bill Holder Phone: {bill.phone_no}
                        </Typography>
                      </Box>
                    </CardContent>
                  </BillCard>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {!loading && filteredBills.length > 0 && (
          <PaginationCard>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Box>
                <Typography variant="body2" sx={{ color: "#6b7280" }}>
                  Showing {(currentPage - 1) * BILLS_PER_PAGE + 1} to{" "}
                  {Math.min(currentPage * BILLS_PER_PAGE, filteredBills.length)}{" "}
                  of {filteredBills.length} bills
                  {gstBillsOnly && (
                    <Chip
                      label="GST Bills Only"
                      size="small"
                      sx={{
                        ml: 2,
                        backgroundColor: "rgba(5, 150, 105, 0.1)",
                        color: "#059669",
                      }}
                    />
                  )}
                </Typography>
              </Box>
              <StyledPagination
                count={totalPages}
                page={currentPage}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Stack>
          </PaginationCard>
        )}

        {/* No Results */}
        {!loading && filteredBills.length === 0 && (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Receipt
              sx={{
                fontSize: "4rem",
                color: "rgba(255, 255, 255, 0.6)",
                mb: 2,
              }}
            />
            <Typography
              variant="h6"
              sx={{ color: "rgba(255, 255, 255, 0.8)", mb: 1 }}
            >
              No bills found
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.6)" }}
            >
              {searchTerm ||
              paymentFilter !== "all" ||
              dateFilter ||
              gstBillsOnly
                ? "Try adjusting your filters to see more results"
                : "No bills have been created yet"}
            </Typography>
            {(searchTerm ||
              paymentFilter !== "all" ||
              dateFilter ||
              gstBillsOnly) && (
              <Button
                variant="outlined"
                onClick={clearFilters}
                sx={{
                  mt: 2,
                  borderColor: "rgba(255, 255, 255, 0.6)",
                  color: "rgba(255, 255, 255, 0.8)",
                  "&:hover": {
                    borderColor: "rgba(255, 255, 255, 0.8)",
                    backgroundColor: "rgba(255, 255, 255, 0.1)",
                  },
                }}
              >
                Clear All Filters
              </Button>
            )}
          </Box>
        )}

        {/* Toast Notification */}
        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setToast({ ...toast, open: false })}
            severity={toast.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {toast.message}
          </Alert>
        </Snackbar>
      </Container>
    </StyledContainer>
  );
};

export default BillsManagement;
